import { useQuery } from '@tanstack/react-query'
import { Copy, Link as LinkIcon, MessageCircle } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { customerAPI, referralAPI } from '../api/index'
import { Button } from '../components/Button'
import { PageHeader } from '../components/PageHeader'
import { formatDate } from '../lib/utils'

export function ReferralPage() {
  const [copied, setCopied] = useState(false)

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => referralAPI.getStats().then(r => r.data),
  })

  const { data: code, isLoading: codeLoading } = useQuery({
    queryKey: ['referral-code'],
    queryFn: () => referralAPI.getCode().then(r => r.data),
  })

  const { data: referrals, isLoading: referralsLoading } = useQuery({
    queryKey: ['referrals'],
    queryFn: () => customerAPI.list({ limit: 100 }).then(r => r.data.customers),
  })

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    toast.success('Copied!')
    setTimeout(() => setCopied(false), 2000)
  }

  const shareOnWhatsApp = () => {
    const message = `Join RTO Manager! Use my referral code: ${code?.referral_code || 'RTO2024'}`
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  return (
    <div>
      <PageHeader title="Refer & Earn" subtitle="Earn rewards by referring new agencies to RTO Manager" />

      {/* Referral Code Card */}
      {codeLoading ? (
        <div className="h-32 bg-gray-200 rounded-lg animate-pulse mb-6" />
      ) : (
        <div className="bg-white rounded-lg border-2 border-saffron p-6 mb-6">
          <p className="text-gray-600 text-sm mb-2">Your Referral Code</p>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <code className="flex-1 bg-gray-50 px-4 py-3 rounded-lg font-mono text-lg font-bold text-gray-900">
              {code?.referral_code || 'N/A'}
            </code>
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                onClick={() => copyToClipboard(code?.referral_code || '')}
                variant="secondary"
                size="sm"
                className="flex-1 md:flex-none"
              >
                <Copy className="w-4 h-4 mr-2" />
                {copied ? 'Copied!' : 'Copy Code'}
              </Button>
              <Button
                onClick={() => copyToClipboard(`https://rto.manager/register?ref=${code?.referral_code || ''}`)}
                variant="secondary"
                size="sm"
                className="flex-1 md:flex-none"
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button
                onClick={shareOnWhatsApp}
                className="flex-1 md:flex-none bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* How It Works */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-3xl mb-2">🔗</div>
            <h3 className="font-semibold text-gray-900">Share Your Code</h3>
            <p className="text-sm text-gray-600 mt-1">Send your referral code to agencies you know</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-3xl mb-2">✅</div>
            <h3 className="font-semibold text-gray-900">They Sign Up</h3>
            <p className="text-sm text-gray-600 mt-1">They register using your referral link</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-3xl mb-2">🎁</div>
            <h3 className="font-semibold text-gray-900">Earn Rewards</h3>
            <p className="text-sm text-gray-600 mt-1">After 3 months, get free subscription months</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-gray-600 text-sm">Total Referrals</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.total_referrals || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-gray-600 text-sm">Rewarded</p>
            <p className="text-3xl font-bold text-green-600 mt-2">{stats?.rewarded || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-gray-600 text-sm">Pending</p>
            <p className="text-3xl font-bold text-amber-600 mt-2">{stats?.pending || 0}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-gray-600 text-sm">Total Months Earned</p>
            <p className="text-3xl font-bold text-saffron mt-2">{stats?.total_months_earned || 0}</p>
          </div>
        </div>
      )}

      {/* Referral History */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Referral History</h3>
        </div>

        {referralsLoading ? (
          <div className="space-y-2 p-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : !referrals || referrals.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-gray-500 p-6">
            <p>No referrals yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-6">Agency Name</th>
                  <th className="text-left py-3 px-6">Status</th>
                  <th className="text-left py-3 px-6">Reward Months</th>
                  <th className="text-left py-3 px-6">Signed Up</th>
                  <th className="text-left py-3 px-6">Rewarded At</th>
                  <th className="text-left py-3 px-6">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {referrals.slice(0, 10).map((ref: any) => (
                  <tr key={ref.id} className="hover:bg-gray-50">
                    <td className="py-3 px-6 font-medium">{ref.full_name}</td>
                    <td className="py-3 px-6">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Pending
                      </span>
                    </td>
                    <td className="py-3 px-6">3</td>
                    <td className="py-3 px-6">{formatDate(ref.created_at)}</td>
                    <td className="py-3 px-6">—</td>
                    <td className="py-3 px-6">—</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
